<script lang="ts">
  import { onMount } from 'svelte';
  import { cn } from '$lib/utils/misc';
  import { EDITOR_HEIGHTS, getPostEditor, MIN_TEXT_AREA_HEIGHT } from '$lib/stores/postEditorStore';
  import { browser } from '$app/environment';

  export let isResizing: boolean;
  export let closeEditor: () => void;
  export let showHotkeyCheatsheet: boolean;

  const MIN_HEIGHT = 470;

  let showEscOverlay: boolean;
  let startY: number;
  let startEditorHeight: number;
  const { openState, editorHeight } = getPostEditor();
  let isMobile = browser ? window.innerWidth < 640 : false;

  function handleMousedown(e: MouseEvent) {
    isResizing = true;
    startY = e.clientY;
    startEditorHeight = $editorHeight;
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleMousemove);
    window.addEventListener('mouseup', handleMouseup);
  }

  function handleMousemove(e: MouseEvent) {
    if (!isResizing) return;
    const delta = startY - e.clientY;
    const newHeight = Math.min(
      Math.max(startEditorHeight + delta, MIN_HEIGHT),
      window.innerHeight * 0.9,
    );
    if (newHeight === MIN_HEIGHT) return;
    editorHeight.set(newHeight);
  }

  function handleMouseup() {
    isResizing = false;
    startY = 0;
    startEditorHeight = $editorHeight;
    document.body.style.userSelect = '';
    window.removeEventListener('mousemove', handleMousemove);
    window.removeEventListener('mouseup', handleMouseup);
  }

  function handleKeydown(e: KeyboardEvent) {
    // Prevent hotkeys from triggering when editor is hidden
    if ($openState === 'hidden') return;

    // Handle cheatsheet toggle
    if (e.altKey && (e.key === '/' || e.code === 'Slash')) {
      e.preventDefault();
      showHotkeyCheatsheet = !showHotkeyCheatsheet;
      return;
    }

    // Handle escape
    if (e.key === 'Escape') {
      e.preventDefault();
      if (showHotkeyCheatsheet) {
        showHotkeyCheatsheet = false;
      } else if ($openState === 'fullscreen') {
        openState.set('normal');
      } else if ($openState === 'normal') {
        closeEditor();
      }
    }

    // Check for cmd/ctrl + f
    if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
      e.preventDefault();
      if ($openState === 'fullscreen') {
        openState.set('normal');
      } else {
        openState.set('fullscreen');
      }
      return;
    }

    // Check for cmd/ctrl + e
    if ((e.metaKey || e.ctrlKey) && e.key === 'm') {
      e.preventDefault();
      if ($openState !== 'minimized') {
        openState.set('minimized');
      } else {
        openState.set('normal');
      }
      return;
    }
  }

  /**
   * Force fullscreen when the screen is resized to mobile
   */
  function handleMobile() {
    if (!browser) return;
    isMobile = window.innerWidth < 640;
    if (isMobile && $openState === 'normal') {
      openState.set('fullscreen');
    }
  }

  onMount(() => {
    window.addEventListener('keydown', handleKeydown);
    window.addEventListener('resize', handleMobile);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('resize', handleMobile);
      document.body.style.overflow = '';
    };
  });

  openState.subscribe((state) => {
    const isDesktop = !isMobile;

    // Force fullscreen on mobile
    if (isMobile && state === 'normal') {
      openState.set('fullscreen');
    }

    // Prevent background scrolling on fullscreen
    if (state === 'fullscreen') {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // Show esc overlay on desktop fullscreen
    if (state === 'fullscreen' && isDesktop) {
      showEscOverlay = true;
    } else {
      showEscOverlay = false;
    }

    if (state === 'normal') {
      editorHeight.set(EDITOR_HEIGHTS.normal);
    }

    if (state === 'minimized') {
      editorHeight.set(EDITOR_HEIGHTS.minimized);
    }
  });
</script>

{#if showEscOverlay}
  <div
    class={cn(
      'bg-gray-dark-4 text-gray-dark-12 fixed left-1/2 top-4 z-[60] -translate-x-1/2 rounded-md px-4 py-2 text-sm',
      'animate-esc-overlay',
    )}
    role="alert"
  >
    Press ESC to exit expanded view
  </div>
{/if}

{#if $openState === 'normal'}
  <div
    role="button"
    tabindex="0"
    class={cn(
      'hover:bg-gray-dark-3 absolute left-0 right-0 top-0 flex h-4 cursor-ns-resize items-center justify-center',
    )}
    on:mousedown={handleMousedown}
  >
    <div class="bg-gray-dark-5 h-1 w-20 rounded-full" />
  </div>
{/if}

<style lang="postcss">
  @keyframes esc-overlay {
    0% {
      opacity: 0;
    }
    10% {
      opacity: 0.9;
    }
    80% {
      opacity: 0.9;
    }
    100% {
      opacity: 0;
    }
  }

  :global(.animate-esc-overlay) {
    animation: esc-overlay 2.2s ease-in-out forwards;
  }
</style>
